import { useState, useEffect, useContext, useRef, useMemo} from 'react';
import {Box, Button, Dialog, DialogContent, DialogTitle, Tabs, Tab, Tooltip, Typography} from '@mui/material';
import ProgressSpinner from '../Common/ProgressSpinner';
import BasicDatatable from '../Common/BasicDatatable';
import { MRT_ColumnDef } from 'material-react-table';
import { useTheme } from '@mui/material/styles';
import GlobalSearchService from "../../services/integrations/GlobalSearchService";
import { Link} from 'react-router-dom';
import useGlobalContext from '../../context/useGlobalContext';
import { capitalizeFirstLetter, convertToUTC, dateFromatter, getChartSeries, getSentimentColor, validateDate } from '../../utils/utils';
import { GlobalContext } from "../../context/GlobalContext.tsx";
import { useLocation } from "react-router-dom";
import { getUserAttributes } from "../../utils/auth.ts";
import { LineChart } from '@mui/x-charts';
import { differenceInDays } from 'date-fns';
import { axisClasses } from '@mui/x-charts';
import '../../styles/common.css'
import ShowChartIcon from '@mui/icons-material/ShowChart';

const Reporting = () =>{
    const location = useLocation();
    const loggedInEmail = getUserAttributes()?.email;
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [meetingRows, setMeetingRows] = useState<any[]>([]);
    const [clientRows, setClientRows] = useState<any[]>([]);
    const [clientRowCount, setClientRowCount] = useState<number>(0);
    const [meetingRowCount, setMeetingRowCount] = useState<number>(0);
    const theme = useTheme();
    const { startEndMeetingTime } = useGlobalContext();
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const initialTab = location.state?.fromTab === 'clients'? 'clients':'meetings';
    const [activeTab, setActiveTab] = useState<"meetings" | "clients">(initialTab);
    const [tabSort, setTabSort] = useState({
        meetings: { sort_field: "start_time", sort_order: "desc" },
        clients: { sort_field: "lastInteraction", sort_order: "desc" }
    });    
    const sort = tabSort[activeTab];
    const { reportingSearch, setReportingSearch } = useContext(GlobalContext);
    const searchTerm = reportingSearch;
    const [selectedClient, setSelectedClient] = useState<string | null>(null);
    const [selectedClientSubOrgId, setSelectedClientSubOrgId] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const startDate = new Date(startEndMeetingTime.start_time);
    const endDate = new Date(startEndMeetingTime.end_time);
    const totalDays = differenceInDays(endDate, startDate) + 1;
    const chartRef = useRef<HTMLDivElement>(null);
    const [viewTab, _setViewTab] = useState(1);
    const enabledModes = useMemo(() => {
        if (totalDays <= 14) return ['day'];
        if (totalDays <= 31) return ['day', 'week'];
        if (totalDays <= 62) return ['week']
        if (totalDays <= 180) return ['week', 'month'];
        return ['month'];
      }, [totalDays]);
    const labelPixelWidths = {
        day: 50,
        week: 110,
        month: 10,
      };
      const [groupMode, setGroupMode] = useState<'day' | 'week' | 'month'>('day');
      const [userSetGroupMode, setUserSetGroupMode] = useState(false);
      useEffect(() => {
        if( viewTab != 0 ){
            return
        }
        setUserSetGroupMode(false);
        if (chartRef.current && !userSetGroupMode) {
          const width = chartRef.current.offsetWidth;          
          const maxDayLabels = Math.floor(width / labelPixelWidths.day);
          const maxWeekLabels = Math.floor(width / labelPixelWidths.week);
          const totalWeeks = Math.ceil(totalDays / 7);
      
          if (totalDays > maxDayLabels) {
            if (totalWeeks <= maxWeekLabels) setGroupMode('week');
            else setGroupMode('month');
          } else {
            setGroupMode('day');
          }
        }
      }, [reportingSearch, meetingRows, chartRef.current, viewTab]);

      type GraphDataPoint = {
        date: string; 
        meetingCount: number;
        clientCount: number;
      };

    const { data, isLoading, isSuccess, isFetching } = GlobalSearchService.useGetKeywordSearchResults({
            searchText: searchTerm,
            limit: pageSize,
            offset: pageNumber,
            sortOrder: sort.sort_order,
            sortBy: sort.sort_field,
            startTime: convertToUTC(startEndMeetingTime?.start_time),
            endTime: convertToUTC(startEndMeetingTime?.end_time),
            employeeEmail: loggedInEmail,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            clientSubOrgId: selectedClientSubOrgId || undefined, // Pass selectedClientSubOrgId if it exists
        },true);

    const {
        isLoading: isClientLoading,
        isFetching: isClientFetching,
        isSuccess: isClientSuccess,
        data: clientData
        } = GlobalSearchService.useGetClientKeywordSearchResults({
        searchText: searchTerm,
        limit: pageSize,
        offset: pageNumber,
        sortOrder: sort.sort_order,
        sortBy: sort.sort_field,
        startTime: convertToUTC(startEndMeetingTime?.start_time),
        endTime: convertToUTC(startEndMeetingTime?.end_time),
        employeeEmail: loggedInEmail,
    },true);

    useEffect(() => {
        setShowSpinner( isClientLoading || isClientFetching);
         if ( isClientSuccess && clientData) {
            setClientRows(clientData.clients || []);
            setClientRowCount(clientData.totalRecords || 0);
        }
    }, [activeTab, clientData, isClientSuccess, isClientLoading, isClientFetching]);

        useEffect(() => {
            setShowSpinner(isLoading || isFetching);
            if(isSuccess && data) {
                setMeetingRows(data.interactions || []);
                setMeetingRowCount(data.totalRecords || 0);
                setUserSetGroupMode(false);
            }
        }, [data, isSuccess, isLoading, isFetching]);

        useEffect(() => {
            if(activeTab =='clients' && selectedClient && openDialog){
                setMeetingRows([]);
                setMeetingRowCount(0);
                setUserSetGroupMode(false);
            }
        },[activeTab, selectedClient, openDialog]);

        useEffect(() => {
            const FromMeeting = location.state?.ParentPageIdentifier === "reportingPage";
            if (!FromMeeting) {
                setReportingSearch("");
            }
        }, [location.state]);


        const onPageChange = (value: number) => {
            setPageNumber(value);
        };

        const onPaginationChange = (pageNumber: number) => {
            onPageChange(pageNumber + 1);
        };
        const onSortingChange = (sorting: any[]) => {
            if (sorting && sorting.length > 0) {
                const newSort = {
                sort_field: sorting[0].id,
                sort_order: sorting[0].desc === true ? "desc" : "asc",
                };
                setTabSort(prev => ({
                ...prev,
                [activeTab]: newSort,
                }));
            }
            };
        const handlePageSizeChange = (newPageSize: number) => {
            setPageSize(newPageSize);
        };

        const handleClose = () => {
            setOpenDialog(false);
            setSelectedClient(null);
            setSelectedClientSubOrgId(null);
        };

    const meetingColumns : MRT_ColumnDef<any>[] = [
        {
            accessorKey: "title",
            header: "Interaction Title",
            size: 450,
            minSize: 350,
            maxSize: 350,
            grow: true,
            enableColumnFilter: false,
            Cell: ({ row }: { row: any }) => {
                const isExternal = row.original.isExternal;
                const endTime = row?.original?.endTime;
                const isMeetingOver = endTime && new Date(endTime) < new Date();
                return (
                <Box className="flex flex-row gap-1 justify-between items-start w-full">
                    <Link
                        color="primary"
                        to={`/meeting/${row.original.interactionId}`}
                        state={{is_team_meeting_id: true,
                                keyword: reportingSearch,
                                ParentPageIdentifier: "reportingPage",
                                fromTab: activeTab,
                                isMeetingOver: isMeetingOver,
                        }}
                        style={{
                            flexGrow: 0,
                            flexShrink: 1,
                            flexBasis: "auto",
                            textDecoration: "underline",
                            textDecorationColor: "rgba(25, 118, 210, 0.4)",
                            color: "#1976d2",
                            whiteSpace: "nowrap",
                        }}
                        className="truncate-table-cell-ellipsis"
                    >
                        {row.original.title}
                    </Link>
                    <Box
                    className="flex flex-row justify-center items-center gap-2"
                    sx={{
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: "auto",
                    minWidth: "70px",
                    maxWidth: "70px",
                    display: "inline-block",
                    textAlign: "center",
                    background: "#ffffff",
                    borderRadius: "16px",
                    border: `1px ${
                        isExternal ? theme.palette.brand.main : "#585858"
                    }  solid`,
                    padding: "4px 8px",
                    [theme.breakpoints.down("lg")]: {
                        padding: "2px 6px",
                    },
                    marginRight:"40px",
                    marginLeft:"auto"
                    }}
                >
                    <Typography
                    variant="subtitle1"
                    sx={{
                        color: isExternal ? theme.palette.brand.main : "#222222",
                        fontWeight: "600",
                        wordWrap: "break-word",
                    }}
                    >
                    {isExternal ? "External" : "Internal"}
                    </Typography>
                </Box>
                </Box>
            )},
        },
        {
            accessorKey: "start_time",
            header: "Date",
            size: 350,
            minSize: 300,
            maxSize:350,
            grow: true,
            enableColumnFilter: false,
            Cell: ({row}: {row:any}) => (
                 <div>
                    <span>{validateDate(row.original.startTime, "MMM DD, YYYY hh:mm A")}</span>
                </div>
            ),
        },
        {
            accessorKey: "overallSentiment",
            header: "Call Rating",
            Cell: ({ cell, row }: { cell: any; row: any }) => {
            const sentiment = cell.getValue();
            const color = getSentimentColor(sentiment);
            const { isPreviousAsset, isPresentAsset } = row.original;
            // No recordings at all
            if (!isPreviousAsset && !isPresentAsset) {
                return (
                <Typography className="meeting-sentiment__no-recording">
                    No Recording
                </Typography>
                );
            }

            // Previous recording only
            if (isPreviousAsset && !isPresentAsset) {
                return (
                <Typography className="meeting-sentiment__no-recording">
                    No Recording
                </Typography>
                );
            }

            // Current recording exists
            if (color) {
                return (
                <Box
                    className="meeting-sentiment__box"
                    sx={{ backgroundColor: color }}
                    title={sentiment}
                />
                );
            }

            return (
                <Typography className="meeting-sentiment__no-recording">
                Recording Present
                </Typography>
            );
            }
        },
        
    ]
      const clientColumns: MRT_ColumnDef<any>[] = [
        {
          accessorKey: "clientDomain",
          header: "Client Name",
          size: 330,
          minSize: 330,
          maxSize: 330,
          grow: true,
          enableColumnFilter: false,
          Cell: ({ row }: { row: any }) => {
            const formattedName = capitalizeFirstLetter(row.original?.clientName);
            const clientSubOrgId = row.original?.subOrgId; 
            return (
              <Box className="truncate-table-cell-ellipsis" sx={{ textAlign: "left" }}>
                <Button
                onClick={() => {
                    setSelectedClient(formattedName);
                    setOpenDialog(true);
                    setSelectedClientSubOrgId(clientSubOrgId);
                }}
                sx={{
                    textDecoration: "none",
                    color: "#1976d2",
                    textTransform: "capitalize",
                    fontFamily: 'var(--font-family)',
                    fontWeight: "500",
                }}
                >
                {formattedName}
                </Button>
              </Box>
            );
          },
        },
        {
            accessorKey: "totalNumberOfMeetings",
            header: "Interactions",
            size: 450,
            minSize: 450,
            maxSize: 450,
            grow: true,
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ row }: {row : any}) => {
                const clientDomain = row.original.clientDomain;
                return( 
                <Box sx={{textAlign: "left"}}>
                   <Button
                    onClick={() => {
                        setSelectedClient(clientDomain);
                        setOpenDialog(true);
                        setSelectedClientSubOrgId(row.original?.subOrgId);
                    }}
                    sx={{
                        textDecoration: "none",
                        color: "#1976d2",
                        textTransform: "capitalize",
                        fontFamily: 'var(--font-family)',
                        fontWeight: "500",
                    }}
                    >
                    {row.original.totalNumberOfMeetings || 0}
                    </Button> 
                </Box>
                );
            }
        },
        {
          accessorKey: "lastInteraction",
          header: "Last Interaction",
          size: 300,
          grow: true,
          minSize: 300,
          maxSize: 300,
          enableColumnFilter: false,
          Cell: ({ row }: { row: any }) => {
            const summary = row.original.title;
            return (
              <Box sx={{ textAlign: "left" }}>
                <Tooltip title={summary} placement="top">
                    <Link
                    to={`/meeting/${row.original?.interactionId}`}
                    state={{ 
                        is_team_meeting_id: true,
                        keyword: reportingSearch,
                    }}
                    style={{
                        cursor: "pointer"
                    }}
                    >
                    {dateFromatter(row.original.lastInteraction, "MMM DD, YYYY")}
                    </Link>
                </Tooltip>
              </Box>
            );
          },
        },
    ]

    const rawGraphData: GraphDataPoint[] = data?.graphData ?? [];
    const { xLabels, primarySeries, secondarySeries } = useMemo(() =>
        getChartSeries(rawGraphData, groupMode, startDate, endDate, activeTab),
        [rawGraphData, groupMode, startDate, endDate, activeTab]
    );

    return (
        <Box className="w-full">
        <Box sx={{
          position: "sticky",
          display: "block",
          width: "100%",
          height: "72px",
          left: "220px",
        }}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2}}>
        <Box
            sx={{
            display: "flex",
            flexDirection: "row",
            gap: 5,
            width: 'fit-content',
            mx: 'auto',
            }}
        >
        {[{title: "Total Interactions", value: data?.totalRecords || "--", key: "meetings" },
          {title: "Total Clients", value: clientData?.totalRecords || "--", key: "clients" },
        ].map((item, index) => {
            const isClickable = clientData?.totalRecords >= 0;
            const isActive = activeTab === item.key;
            return (
                <Box
                key={index}
                onClick={isClickable ? () => setActiveTab(item.key as 'meetings' | 'clients') : undefined}
                sx={{
                    border: isActive ? '2px solid #B902CA' : '1px solid #E5E5E5',
                    borderRadius: '15px',
                    padding: 2,
                    minHeight: '100px',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    width: "200px",
                    cursor: isClickable ? 'pointer' : 'default',
                    backgroundColor: isActive ? '#FBE8FB' : '#fff',
                    transition: 'all 0.2s ease-in-out',
                }}
            >
                <Box sx={{ fontWeight: 500, fontFamily: 'var(--font-family)', fontSize: '16px' }}>
                    <Box sx={{ textAlign: 'initial', fontSize: '16px', fontWeight: 500, mb: 1, color: theme.palette.primary.dark }}>
                        {item.title}
                    </Box>
                    <Box sx={{ textAlign: 'center', fontSize: '24px', fontWeight: 1000, color: "#B902CA" }}>
                        {item.value}
                    </Box>
                </Box>
            </Box>
        )})}
        </Box>
        </Box>
        <Box className="headerContainer">
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              value={activeTab}
              onChange={(_event: any, newValue: any) => {
                setActiveTab(newValue);
                setPageNumber(1);
              }}
              TabIndicatorProps={{
                sx: { backgroundColor: '#6036D6' },
              }}
              sx={{
                minHeight: '0px',
                '& .MuiTabs-flexContainer': {
                  gap: '24px',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontFamily: 'var(--font-family)',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#666',
                  minWidth: 'auto',
                  padding: '10px 0px',
                  minHeight: '0px',
                  '&.Mui-selected': {
                    color: '#6036D6',
                  },
                },
              }}
            >
              <Tab value="meetings" label="Interactions" />
              <Tab value="clients" label="Clients" />
            </Tabs>
        </Box>
        </Box>
        {viewTab === 0 && (((activeTab === 'meetings' && data?.graphData?.[0]?.meetingCount > 0) || 
            (activeTab === 'clients' && data?.graphData?.[0]?.clientCount > 0) ) ? (
            <Box ref={chartRef} className="chart-wrapper-box">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 1, fontSize: 13, color: '#888' }}>Group by:</Typography>
                    {['day', 'week', 'month'].map((mode) => {
                        const isEnabled = enabledModes.includes(mode);
                        const isSelected = groupMode === mode;
                        return (
                        <Box key={mode} component="span">
                            <Button
                            variant={isSelected ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => isEnabled && (setGroupMode(mode as 'day' | 'week' | 'month'), setUserSetGroupMode(true))}
                            sx={{
                                textTransform: 'capitalize',
                                ml: 0.5,
                                fontSize: 12,
                                borderRadius: '16px',
                                minWidth: '60px',
                                backgroundColor: isSelected ? '#B902CA' : '#fff',
                                color: isSelected ? '#fff' : isEnabled ? '#555' : '#999',
                                borderColor: '#ccc',
                                opacity: isEnabled ? 1 : 0.6,
                                pointerEvents: isEnabled ? 'auto' : 'none',
                                cursor: isEnabled ? 'pointer' : 'not-allowed',
                                '&:hover': {
                                backgroundColor: isSelected ? '#a001b5' : isEnabled ? '#f5f5f5' : '#fff',
                                },
                            }}
                            >
                            {mode}
                    </Button>
                        </Box>
                    );
                    })}
                </Box>
                </Box>

                <LineChart
                xAxis={[{ scaleType: 'point', data: xLabels, tickLabelStyle: { fontSize: 12, fontFamily: 'var(--font-family)' } }]}
                yAxis={[{ min: 0, tickMinStep: 1, label: 'Count', tickLabelStyle: { fontSize: 12, fontFamily: 'var(--font-family)' } }]}
                series={[secondarySeries, primarySeries]}
                height={320}
                margin={{ right: 60, bottom: 60, left: -5 }}
                grid={{ vertical: false, horizontal: true }}
                sx={{
                    '.MuiChartsAxis-line': { stroke: '#DDD' },
                    '.MuiChartsGrid-line': { stroke: '#F0F0F0' },
                    '.MuiChartsTooltip-root': { fontFamily: 'var(--font-family)', fontSize: '13px' },
                    [`.${axisClasses.bottom} .${axisClasses.tickLabel}`]: {
                    transform: 'rotate(-45deg)',
                    textAnchor: 'end',
                    },
                }}
                />
            </Box>
        ):(<Box className="chart-placeholder-box">
            <ShowChartIcon sx={{ fontSize: 60, color: '#ccc', mb: 1 }} />
            <Typography variant="h3" fontWeight="550" gutterBottom sx={{ fontFamily: 'var(--font-family)' }}>
              No chart data available
            </Typography>
            <Typography variant="body2" sx={{ color: '#777', maxWidth: 400, fontFamily: 'var(--font-family)' }}>
              No trends to show yet. Try changing the keyword or date range.
            </Typography>
          </Box>                  
          ))}
        {viewTab === 1 && (
            <Box 
            sx={{
                border: "1px solid #E5E5E5",
                backgroundColor: "#fff",
                animation: "fadeSlideZoomIn 0.5s cubic-bezier(0.33, 1, 0.68, 1)",
            }}
            >
                <BasicDatatable
                    columns={activeTab === 'meetings' ? meetingColumns : clientColumns}
                    rows={activeTab === 'meetings' ? meetingRows : clientRows}
                    rowCount={activeTab === 'meetings' ? meetingRowCount : clientRowCount}
                    hidePaginationFooter={meetingRowCount <= 1 ? true : false}
                    onSortingChange={onSortingChange}
                    onPageSizeChange={(newPageSize: number | null | undefined) => {
                        if (newPageSize !== null && newPageSize !== undefined) {
                            handlePageSizeChange(newPageSize);
                            }
                        }}
                    onPaginationChange={onPaginationChange}
                    isBordervisible={true}
                />
            </Box>
            )}
        </Box>
            <Dialog open={openDialog} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontFamily: 'var(--font-family)',
                fontSize: "18px",
                color: "#333",
                }}
            >
                <Box>
                Interactions for {capitalizeFirstLetter(selectedClient?.split(".")?.[0] || "")}
                </Box>
                <button
                onClick={handleClose}
                className="summary-dialog-box__close-btn"
                style={{ top: "auto" }}
                >
                &times;
                </button>
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
                <BasicDatatable
                columns={meetingColumns}
                rows={meetingRows}
                rowCount={meetingRowCount}
                onSortingChange={onSortingChange}
                onPageSizeChange={(newPageSize: number | null | undefined) => {
                    if (newPageSize !== null && newPageSize !== undefined) {
                    handlePageSizeChange(newPageSize);
                    }
                }}
                onPaginationChange={onPaginationChange}
                isBordervisible={true}
                />
            </DialogContent>
            </Dialog>
        <ProgressSpinner showSpinner={showSpinner} />
    </Box>
    );

};
export default Reporting;